import React from "react";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import AgriScoreWidget from "@/components/aieco/AgriScoreWidget";
import AgriHistory from "@/components/aieco/AgriHistory";
import KpiCard from "@/components/aieco/KpiCard";
import TokenIntelligence from "@/components/aieco/TokenIntelligence";
import RiskSummary from "@/components/aieco/RiskSummary";
import EventFeed from "@/components/aieco/EventFeed";
import { recentEvents } from "@/data/mockData";
import { useWorkspace } from "@/context/WorkspaceContext";
import useLiveEvents from "@/hooks/useLiveEvents";
import { Sparkles } from "lucide-react";

export default function CommandCenter() {
  const { data } = useWorkspace();
  const { events, connected } = useLiveEvents(recentEvents);
  const { t } = useTranslation();
  return (
    <div className="mx-auto max-w-[1480px] px-6 py-8 lg:px-10 lg:py-10" data-testid="page-command-center">
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className="flex flex-wrap items-end justify-between gap-4"
      >
        <div>
          <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.18em] text-slate-400">
            <Sparkles size={12} className="text-[#5B8DEF]" aria-hidden="true" />
            {t("command.subtitle")} · {data.name}
          </div>
          <h1 className="mt-2 font-display text-3xl font-medium tracking-tight text-white sm:text-4xl">
            {t("command.title")}
          </h1>
          <p className="mt-2 max-w-2xl text-sm text-slate-400">
            {t("command.lede")}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button className="rounded-lg border border-white/5 bg-white/[0.02] px-3 py-2 text-[12px] text-slate-200 hover:border-white/10" data-testid="cmd-export">
            {t("command.exportSnapshot")}
          </button>
          <button className="rounded-lg bg-[#5B8DEF] px-3 py-2 text-[12px] font-medium text-[#0b1020] hover:bg-[#7AA4F5]" data-testid="cmd-investigate">
            {t("command.investigate")}
          </button>
        </div>
      </motion.div>

      <div className="mt-8 grid grid-cols-12 gap-6">
        <div className="col-span-12 lg:col-span-5">
          <AgriScoreWidget score={data.agriScore} breakdown={data.agriBreakdown} />
        </div>
        <div className="col-span-12 lg:col-span-7">
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
            {data.kpis.map((k, i) => (
              <KpiCard key={k.key} k={k} index={i} />
            ))}
          </div>
        </div>
      </div>

      <div className="mt-6 grid grid-cols-12 gap-6">
        <div className="col-span-12 xl:col-span-8">
          <TokenIntelligence stats={data.tokenStats} />
        </div>
        <div className="col-span-12 xl:col-span-4">
          <RiskSummary items={data.riskSummary} />
        </div>
      </div>

      <div className="mt-6 grid grid-cols-12 gap-6">
        <div className="col-span-12 xl:col-span-5">
          <AgriHistory />
        </div>
        <div className="col-span-12 xl:col-span-7">
          <EventFeed events={events} connected={connected} />
        </div>
      </div>
    </div>
  );
}
